import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  ChevronLeft,
  Ticket,
  Phone,
  MessageCircle,
  Share2,
  MapPin,
  Clock,
  CalendarDays,
  Globe,
  CircleAlert,
  TicketPercent,
} from 'lucide-react';
import { FaInstagram, FaFacebook, FaYoutube } from 'react-icons/fa';

import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Skeleton } from '@/Components/ui/skeleton';
import { Badge } from '@/Components/ui/badge';
import { Modal, Spinner } from '@/Components/Common/Tailwind';
import { getPageLayout } from '@/config/page-layout';
import Navbar from '@/Components/Common/navbar';
import NavBottomBar from '@/Components/Common/NavBottomBar';
import MobileViewContainer from '@/Components/MobileViewContainer';
import { useUserStore } from '@/store/store';
import KakaoMap from '@/pages/CampMap/KaKaoMap';
import { API } from '@/api';
import { noticeService } from '@/api/services/notice.service';
import { couponService } from '@/api/services/coupon.service';
import { eventService } from '@/api/services/event.service';
import { SITE_ID, SUBDOMAIN } from '@/assets/meta/meta';
import { BsHouseGear } from 'react-icons/bs';
import KakaoIcon from '@/assets/social/kakao_icon.svg';
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { isMobile } from "react-device-detect";
import { stripHtmlTags } from "@/utils/text";
import { handleCopyClipBoard } from "@/common/utils/clipboard";
import {
  getKakaoMapUrl,
  getNaverMapUrl,
  getTmapUrl,
  resolvePreferredMapUrl,
} from "@/utils/mapLinks";
import KakaoMapIcon from "@/assets/icons/kakaomap.svg";
import NaverMapIcon from "@/assets/icons/navermap.svg";
import TmapIcon from "@/assets/icons/tmap.svg";

import FacilitySection from './FacilitySection';
import DisplayMenuSection from './DisplayMenuSection';
import RecommendedReviewSection from './RecommendedReviewSection';
import CarouselGalleryFeed from './CarouselGalleryFeed';
import { SectionHeader, SectionMoreLink } from './SectionHeader';
import { AnnouncementBottomSheet } from '@/Components/Announcement/AnnouncementBottomSheet';
import { DirectionsSection } from '@/Components/DirectionsSection';
import { getLegalDocumentHref, isExternalLegalHref } from '@/pages/Legal/legalDocuments';
import {
  buildOperatingHourRows,
  getApplicableOperatingHourKey,
  isCurrentlyOpen,
} from './operatingHours.mjs';
import {
  formatDisplayHolidayRowText,
  getDisplayHolidayMonthParams,
  getDisplayHolidayRows,
  getDisplayHolidaySourceLabel,
  normalizeDisplayHolidayResponse,
} from '@/common/siteDisplayHolidays.mjs';

const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * SmartLanding - Configuration-driven landing page
 */

// Section Components
function HeroCarouselSection({ slides, isLoading, mainTitleColor }) {
  const [activeSlide, setActiveSlide] = useState(0);
  const touchStartXRef = useRef(null);
  const touchDeltaXRef = useRef(0);

  const slideCount = slides?.length || 0;

  const goToSlide = (index) => {
    if (!slideCount) return;
    setActiveSlide(((index % slideCount) + slideCount) % slideCount);
  };

  useEffect(() => {
    if (!slideCount) return;
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slideCount);
    }, 5000);
    return () => clearInterval(timer);
    // activeSlide를 의존성에 포함해 수동 스와이프 후 자동 전환 타이머를 리셋한다.
  }, [slideCount, activeSlide]);

  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchDeltaXRef.current = 0;
  };

  const handleTouchMove = (e) => {
    if (touchStartXRef.current === null) return;
    touchDeltaXRef.current = e.touches[0].clientX - touchStartXRef.current;
  };

  const handleTouchEnd = () => {
    const delta = touchDeltaXRef.current;
    const SWIPE_THRESHOLD = 50;
    if (Math.abs(delta) > SWIPE_THRESHOLD) {
      goToSlide(activeSlide + (delta < 0 ? 1 : -1));
    }
    touchStartXRef.current = null;
    touchDeltaXRef.current = 0;
  };

  if (isLoading) {
    return <Skeleton className="w-full aspect-16/11 rounded-none" />;
  }

  if (!slides?.length) return null;

  return (
    <section
      className="relative w-full aspect-16/11 overflow-hidden bg-gray-100 touch-pan-y select-none"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {slides.map((slide, idx) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-opacity duration-700 ${idx === activeSlide ? 'opacity-100' : 'opacity-0'
            }`}
        >
          <img
            src={slide.image}
            alt={`Slide ${idx + 1}`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/10" />
        </div>
      ))}

      {/* Floating Info Overlay */}
      {/* <div className="absolute top-4 left-4 right-4 flex justify-between items-start pointer-events-none">
        <Badge className="bg-black/30 backdrop-blur-md text-white border-white/20">
          <span className="w-2 h-2 rounded-full bg-white/60 mr-1.5 animate-pulse" />
          Open Now
        </Badge>
        <button className="w-9 h-9 bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/20 pointer-events-auto">
          <Share2 size={16} />
        </button>
      </div> */}

      {/* Hero Text Overlay */}
      <div className="absolute inset-x-0 bottom-12 px-6 pointer-events-none">
        <h1
          className="text-2xl font-black font-gmarket drop-shadow-lg text-balance"
          style={{ color: mainTitleColor || '#FFFFFF' }}
        >
          {slides[activeSlide]?.title || ""}
        </h1>
      </div>

      {/* Dots */}
      <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-1.5">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setActiveSlide(idx)}
            className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${idx === activeSlide ? 'w-4 bg-white' : 'w-1.5 bg-white/40'
              }`}
          />
        ))}
      </div>
    </section>
  );
}

function StoreIntroSection({ storeInfo, siteDetail, isLoading, showGroupReservationAction = false }) {
  const [holidayDates, setHolidayDates] = useState([]);
  const [displayHolidayData, setDisplayHolidayData] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const year = new Date().getFullYear();
    API.get(`/package/holiday/${year}/`)
      .then((response) => {
        if (!isMounted) return;
        setHolidayDates(Array.isArray(response.data?.result) ? response.data.result : []);
      })
      .catch(() => {
        if (!isMounted) return;
        setHolidayDates([]);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!SITE_ID) return undefined;

    let isMounted = true;
    const { year, month } = getDisplayHolidayMonthParams(new Date());
    API.get(`/sites/display-holidays/?site_id=${SITE_ID}&year=${year}&month=${month}`)
      .then((response) => {
        if (!isMounted) return;
        setDisplayHolidayData(normalizeDisplayHolidayResponse(response.data));
      })
      .catch(() => {
        if (!isMounted) return;
        setDisplayHolidayData(normalizeDisplayHolidayResponse());
      });
    return () => {
      isMounted = false;
    };
  }, []);

  if (isLoading) {
    return (
      <section className="px-6">
        <Skeleton className="h-40 w-full rounded-2xl" />
      </section>
    );
  }

  const phoneNumber = storeInfo?.phone?.replace(/[^\d+]/g, "");
  const hasPhone = Boolean(phoneNumber);
  const hasKakaoChannel = Boolean(storeInfo?.kakaoChannelId);

  const showAddress = siteDetail?.show_address !== false;
  const address = storeInfo?.address;
  const operatingHourRows = buildOperatingHourRows(siteDetail);
  const applicableOperatingHourKey = getApplicableOperatingHourKey(new Date(), holidayDates);
  const displayHolidayRows = getDisplayHolidayRows(displayHolidayData, "all", {
    includePublicHolidays: false,
  }).slice(0, 8);
  const kakaoMapUrl = resolvePreferredMapUrl(
    siteDetail?.kakao_place_url,
    getKakaoMapUrl(siteDetail?.name || storeInfo?.title, siteDetail?.map_marker),
  );
  const naverMapUrl = resolvePreferredMapUrl(
    siteDetail?.naver_place_url,
    getNaverMapUrl(address),
  );
  const tmapUrl = getTmapUrl(siteDetail?.name || storeInfo?.title, siteDetail?.map_marker);
  const hasMapButtons = Boolean(kakaoMapUrl || naverMapUrl || tmapUrl);

  const copyCurrentUrl = async () => {
    await handleCopyClipBoard(window.location.href, {
      successMessage: "링크를 복사했어요.",
      errorMessage: "링크 복사에 실패했어요.",
    });
  };

  const handlePhoneCall = () => {
    if (!hasPhone) return;
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleConsult = () => {
    if (hasKakaoChannel) {
      window.open(`https://pf.kakao.com/${storeInfo.kakaoChannelId}`, "_blank");
      return;
    }
    handlePhoneCall();
  };

  const handleShare = async () => {
    const shareTitle = storeInfo?.title || siteDetail?.name || document.title || "AmazingTicket";

    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: `${shareTitle} 페이지를 확인해보세요.`,
          url: window.location.href,
        });
        return;
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
      }
    }

    await copyCurrentUrl();
  };

  const actionButtonClass = `flex-1 min-w-0 h-11 rounded-2xl bg-white ${showGroupReservationAction ? 'text-[12px]' : 'text-[13px]'} font-poppins font-bold gap-1.5 border-gray-200 text-nearblack shadow-sm hover:bg-gray-50 active:scale-[0.96] transition-[transform,background-color] duration-150`;

  return (
      <Card className="border bg-gray-50 border-gray-100 shadow-sm overflow-hidden mx-6">
        <CardContent className="p-6 flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xl font-black font-gmarket text-nearblack text-balance">
              {storeInfo?.title || "매장 소개"}
            </h2>
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-full border-gray-200 bg-white px-3 text-sm font-bold text-gray-700 shadow-sm hover:bg-gray-50"
              onClick={handleShare}
            >
              <Share2 size={14} className="mr-1.5" /> 공유
            </Button>
          </div>

          <p className="text-4 text-gray-900 leading-relaxed whitespace-pre-wrap text-pretty">
            {storeInfo?.description || "매장 소개 문구를 입력해 주세요."}
          </p>

          {/* Amenities */}
          {siteDetail?.amenities_info && siteDetail.amenities_info.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {siteDetail.amenities_info.map((amenity, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-100 rounded-lg shadow-sm"
                >
                  {amenity.icon && <img src={amenity.icon} alt="" className="w-3.5 h-3.5 object-contain" />}
                  <span className="text-xs font-bold text-gray-700">{amenity.name}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col gap-3">
            {((showAddress && address) || hasMapButtons) && (
              <div className="flex flex-col gap-2">
                {showAddress && address ? (
                  <div className="flex items-start gap-2.5 text-sm text-gray-700">
                    <div className="w-7 h-7 rounded-full  flex items-center justify-center shrink-0">
                      <MapPin size={16} className="text-gray-700" />
                    </div>
                    <span className="block leading-relaxed wrap-break-word pt-1">{address}</span>
                  </div>
                ) : null}
                {hasMapButtons ? (
                  <div className="flex flex-wrap gap-2 justify-end">
                    {kakaoMapUrl ? (
                      <Button
                        asChild
                        variant="outline"
                        className="h-7 rounded-full border-[#F3D548] bg-[#FEE500] px-3 text-xs font-bold text-[#3A2E00] hover:bg-[#FADA0A]"
                      >
                        <a href={kakaoMapUrl} target="_blank" rel="noopener noreferrer">
                          <img src={KakaoMapIcon} alt="kakao map" className="h-3.5 w-3.5" />
                          Kakao
                        </a>
                      </Button>
                    ) : null}
                    {naverMapUrl ? (
                      <Button
                        asChild
                        variant="outline"
                        className="h-7 rounded-full border-[#CFE7D1] bg-[#F3FBF4] px-3 text-xs font-bold text-[#03C75A] hover:bg-[#E3F6E6]"
                      >
                        <a href={naverMapUrl} target="_blank" rel="noopener noreferrer">
                          <img src={NaverMapIcon} alt="naver map" className="h-3.5 w-3.5" />
                          Naver
                        </a>
                      </Button>
                    ) : null}
                    {tmapUrl ? (
                      <Button
                        asChild
                        variant="outline"
                        className="h-7 rounded-full border-[#FFD7CC] bg-[#FFF4F1] px-3 text-xs font-bold text-[#FF6B3D] hover:bg-[#FFE7E0]"
                      >
                        <a href={tmapUrl} target="_blank" rel="noopener noreferrer">
                          <img src={TmapIcon} alt="t map" className="h-3.5 w-3.5" />
                          Tmap
                        </a>
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
            {operatingHourRows.length > 0 && (
              <div className="flex items-start gap-2.5 text-sm text-gray-500">
                <div className="w-7 h-7 rounded-full flex items-center justify-center bordershrink-0">
                  <Clock size={16} className="text-gray-700" />
                </div>
                <div className="flex flex-col gap-1">
                  {operatingHourRows.map((row) => {
                    const shouldShowBadge = row.key === "legacy" || row.key === applicableOperatingHourKey;
                    const currentOpenState = shouldShowBadge ? isCurrentlyOpen(row.text) : null;
                    return (
                      <div key={row.key} className="flex flex-wrap items-center gap-2">
                        {row.label ? <span className="font-bold text-gray-700">{row.label}</span> : null}
                        <span className='text-gray-700 items-start gap-2 text-sm'>{row.text}</span>
                        {currentOpenState === true && (
                          <Badge className="bg-green-50 text-xs px-2.5 py-0.5 ml-1 text-green-700 font-semibold">
                            <span className="w-2 h-2 rounded-full bg-green-300 mr-1 animate-pulse" />
                            현재 영업중
                          </Badge>
                        )}
                        {currentOpenState === false && (
                          <Badge className="bg-gray-400 text-white text-xs px-1.5 py-0.5 ml-1">
                            <span className="w-2 h-2 rounded-full bg-gray-500 mr-0.5" />
                            영업 종료
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {displayHolidayRows.length > 0 && (
              <div className="flex items-start gap-2.5 text-sm text-gray-500">
                <div className="w-7 h-7 rounded-full flex items-center justify-center hrink-0">
                  <CalendarDays size={16} className="text-gray-700" />
                </div>
                <div className="flex flex-col gap-2">
                  <span className="font-bold text-gray-700">휴일 안내</span>
                  <div className="flex flex-wrap gap-1.5">
                    {displayHolidayRows.map((row) => (
                      <span
                        key={row.key || `${row.source}-${row.date || row.weekday}`}
                        className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700"
                      >
                        {formatDisplayHolidayRowText(row)}
                        <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
                          {getDisplayHolidaySourceLabel(row)}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-auto flex flex-col gap-2">
            <div className="flex gap-2">
              <Button
                variant="secondary"
                className={actionButtonClass}
                onClick={handlePhoneCall}
                disabled={!hasPhone}
              >
                <Phone size={16}/> 전화하기
              </Button>
              <Button
                variant="secondary"
                className={actionButtonClass}
                onClick={handleConsult}
                disabled={!hasKakaoChannel && !hasPhone}
              >
                <MessageCircle size={16} /> 상담하기
                {/* <img src={KakaoIcon}alt="kakao talk" className="h-4 w-4" />
                상담하기 */}
              </Button>
              <Button
                asChild
                variant="secondary"
                className={actionButtonClass}
              >
                <Link to="/reservation">
                  <CalendarDays size={16} /> 예약하기
                </Link>
              </Button>
            </div>
            {showGroupReservationAction ? (
              <Button
                asChild
                variant="secondary"
                className="h-11 w-full rounded-2xl border-gray-200 bg-white text-[13px] font-poppins font-bold text-nearblack shadow-sm transition-all hover:bg-gray-50 active:scale-95"
              >
                <Link to="/group-reservation">
                  <CalendarDays size={16} /> 단체예약 문의
                </Link>
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

  );
}

function NoticeListSection({ notices, isLoading, limit = 5, onNoticeClick }) {
  if (isLoading) {
    return (
      <section className="px-6">
        <div className="flex justify-between items-center mb-4">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      </section>
    );
  }

  const formatNoticeDate = (notice) => {
    const dateValue = notice?.date || notice?.created_at;
    if (!dateValue) return "";
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleDateString("ko-KR");
  };

  const visibleNotices = (notices || [])
    .filter(n => n.is_visible !== false)
    .slice(0, limit);

  return (
    <section className="p-6 pb-8">
      <SectionHeader title="공지사항" action={<SectionMoreLink to="/notice" />} />
      {visibleNotices.length > 0 ? (
        <div className="-mx-6 px-6 overflow-hidden">
          <Swiper
            spaceBetween={12}
            slidesPerView="auto"
            className="overflow-visible!"
          >
            {visibleNotices.map((notice, idx) => {
              const titleText = stripHtmlTags(notice.title || "");
              const itemContent = (
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 mb-1 min-h-[2.5rem]">
                      {notice.is_important && (
                        <Badge variant="destructive" className="text-[10px] px-1.5 py-0.5 shrink-0 mt-0.5">
                          중요
                        </Badge>
                      )}
                      <h3 className="text-sm font-bold text-gray-900 line-clamp-2 group-hover:text-black">
                        {titleText}
                      </h3>
                    </div>
                    <p className="text-xs text-gray-500 font-medium tabular-nums">
                      {formatNoticeDate(notice)}
                    </p>
                  </div>
                  <ChevronRight size={16} className="text-gray-300 group-hover:text-zinc-900 transition-colors self-center" />
                </div>
              );

              const cardClass = "animate-fade-up w-full h-full text-left p-4 bg-white rounded-2xl border border-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_16px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.05),0_12px_28px_-6px_rgba(0,0,0,0.12)] transition-[box-shadow,transform] duration-200 group active:scale-[0.96]";

              if (onNoticeClick) {
                return (
                  <SwiperSlide key={notice.id} className="w-65! sm:w-80!">
                    <button
                      type="button"
                      onClick={() => onNoticeClick(notice)}
                      style={{ animationDelay: `${idx * 60}ms` }}
                      className={cardClass}
                    >
                      {itemContent}
                    </button>
                  </SwiperSlide>
                );
              }

              return (
                <SwiperSlide key={notice.id} className="w-65! sm:w-80!">
                  <Link
                    to={notice?.id ? `/notice/detail/${notice.id}` : "/notice"}
                    style={{ animationDelay: `${idx * 60}ms` }}
                    className={`block ${cardClass}`}
                  >
                    {itemContent}
                  </Link>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-2xl bg-gray-50 px-6 py-10 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
            <CircleAlert size={22} className="text-gray-400" />
          </div>
          <p className="mt-3 text-sm font-bold text-gray-900">등록된 공지사항이 없어요</p>
          <p className="mt-1 text-xs text-gray-500">새로운 소식이 올라오면 알려드릴게요.</p>
        </div>
      )}
    </section>
  );
}

function CouponSection({ couponBooks, voucherBooks, isLoading, limit = 4 }) {
  if (isLoading) {
    return (
      <section className="p-6 pb-8">
        <Skeleton className="h-6 w-32 mb-5" />
        <div className="-mx-6 px-6 overflow-hidden">
          <div className="flex gap-3">
            {[1, 2].map(i => (
              <Skeleton key={i} className="h-24 w-64 rounded-2xl shrink-0" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  const discountCouponBooks = (couponBooks || []).map((item) => {
    const isPrice = item.coupon_product?.discount_type === "price";
    return {
      id: `coupon-${item.id}`,
      source: "coupon",
      title: item.coupon_product?.title || "할인쿠폰",
      detail: isPrice
        ? `${item.coupon_product?.discount_amount?.toLocaleString?.() || 0}원 할인쿠폰`
        : `${item.coupon_product?.discount_rate || 0}% 할인쿠폰`,
      amountMain: isPrice
        ? `${item.coupon_product?.discount_amount?.toLocaleString?.() || 0}`
        : `${item.coupon_product?.discount_rate || 0}%`,
      amountSub: isPrice ? "원 할인" : "할인",
      path: "/coupon-book?tab=coupon",
      badge: "할인쿠폰북",
      isNew: Boolean(item.is_new),
    };
  });

  const freeCouponBooks = (voucherBooks || []).map((item) => ({
    id: `voucher-${item.id}`,
    source: "voucher",
    title: item.voucher_product?.name || "무료쿠폰",
    detail: "무료쿠폰",
    amountMain: "무료",
    amountSub: "FREE",
    path: "/coupon-book?tab=voucher",
    badge: "무료쿠폰북",
    isNew: false,
  }));

  const mergedItems = [...freeCouponBooks, ...discountCouponBooks].slice(0, limit);

  return (
    <section className="flex flex-col gap-2 p-6 pb-8">
      <SectionHeader title="쿠폰" />

      {mergedItems.length > 0 ? (
        <div className="-mx-6 px-6 pt-1 pb-5 overflow-hidden">
          <Swiper
            spaceBetween={12}
            slidesPerView="auto"
            className="overflow-visible!"
          >
            {mergedItems.map((coupon) => {
              const isVoucher = coupon.source === "voucher";
              const stub = isVoucher
                ? "bg-rose-50 text-rose-600"
                : "bg-orange-50 text-amber-600";
              return (
                <SwiperSlide key={coupon.id} className="w-64! sm:w-72!">
                  <Link
                    to={coupon.path}
                    className="relative flex h-[104px] overflow-hidden rounded-2xl bg-white shadow-[0_8px_20px_-6px_rgba(17,20,24,0.18)] ring-1 ring-black/5 active:scale-[0.98] transition-transform"
                  >
                    {/* 혜택 블록 (소프트 틴트 스텁) */}
                    <div className={`flex w-[92px] shrink-0 flex-col items-center justify-center gap-0.5 ${stub}`}>
                      <b className="text-2xl font-black leading-none tracking-tight tabular-nums">{coupon.amountMain}</b>
                      <span className="text-[11px] font-bold">{coupon.amountSub}</span>
                    </div>

                    {/* 절취 점선 */}
                    <span className="pointer-events-none absolute left-[92px] top-2.5 bottom-2.5 border-l-2 border-dashed border-gray-300" />

                    {/* 절취 노치 (점선 위/아래, 가장자리를 파먹는 반원) */}
                    <span className="absolute left-[86px] -top-1.5 h-3 w-3 rounded-full" />
                    <span className="absolute left-[86px] -bottom-1.5 h-3 w-3 rounded-full" />

                    {/* 본문 */}
                    <div className="flex flex-1 min-w-0 flex-col justify-center gap-1 px-3.5">
                      <h3 className="truncate text-sm font-bold text-gray-900">{coupon.title}</h3>
                      <span className="truncate text-[11.5px] font-semibold text-gray-400">{coupon.badge}</span>
                    </div>

                    {coupon.isNew ? (
                      <span className="absolute right-2.5 top-2.5 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-black text-white shadow-sm">
                        NEW
                      </span>
                    ) : null}
                  </Link>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      ) : (
        <div className="py-2 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
          <p className="text-sm text-gray-700 font-medium">현재 진행 중인 할인쿠폰/무료쿠폰이 없습니다.</p>
        </div>
      )}
      <div className="mb-3">
        <Link
          to="/coupon-book?tab=voucher"
          className="flex items-center justify-center gap-3 rounded-xl px-3 py-3 bg-orange-100 shadow-xl shadow-orange-50 active:scale-[0.98] transition-transform"
        >
          <TicketPercent size={28} className="text-orange-500 shrink-0" />
          
          <p className="text-lg font-black text-gray-900">전체 쿠폰북 확인하기</p>
          <ChevronRight size={20} className="text-orange-500 shrink-0" />
        </Link>
      </div>
    </section>
  );
}

function GroupReservationLinkSection({ isLoading }) {
  if (isLoading) {
    return (
      <section className="px-6">
        <Skeleton className="h-24 w-full rounded-2xl" />
      </section>
    );
  }

  return (
    <section className="px-6">
      <Link
        to="/group-reservation"
        className="flex items-center justify-between gap-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4 shadow-sm active:scale-[0.98] transition-transform"
      >
        <div className="min-w-0">
          <p className="text-xs font-bold text-emerald-700">단체 방문</p>
          <h2 className="mt-1 text-lg font-black text-gray-950">단체 예약 신청</h2>
          <p className="mt-1 text-sm font-medium text-gray-600">희망 일정과 인원을 남겨주세요.</p>
        </div>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-emerald-700 shadow-sm">
          <ChevronRight size={20} />
        </div>
      </Link>
    </section>
  );
}

function SnsLinksSection({ snsLinks, isLoading }) {
  if (isLoading) {
    return (
      <section className="px-6">
        <Skeleton className="h-6 w-32 mb-4" />
        <div className="flex gap-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-12 flex-1 rounded-xl" />
          ))}
        </div>
      </section>
    );
  }

  if (!snsLinks || snsLinks.length === 0) return null;

  const getSnsIcon = (sns) => {
    switch (sns) {
      case 'instagram':
        return <FaInstagram size={20} />;
      case 'facebook':
        return <FaFacebook size={20} />;
      case 'youtube':
        return <FaYoutube size={20} />;
      case 'blog':
        return <Globe size={20} />;
      case 'kakaotalk':
        return (
          <img src={KakaoIcon} alt="kakao" className="w-5 h-5" />
        );
      default:
        return <Globe size={20} />;
    }
  };

  const getSnsLabel = (sns) => {
    switch (sns) {
      case 'instagram':
        return '인스타그램';
      case 'facebook':
        return '페이스북';
      case 'youtube':
        return '유튜브';
      case 'blog':
        return '블로그';
      case 'kakaotalk':
        return '카카오톡';
      default:
        return 'SNS';
    }
  };

  return (
    <div className="px-6 pb-8">
      <div className="flex flex-wrap gap-x-4 gap-y-3">
        {snsLinks.map((link) => (
          <a
            key={link.id}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-white transition-transform duration-150 active:scale-[0.98]"
          >
            <div className={`w-8 h-8 flex items-center justify-center shrink-0`}>
              {getSnsIcon(link.sns)}
            </div>
            <span className="text-xs font-bold text-gray-900 whitespace-nowrap">{getSnsLabel(link.sns)}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

function LandingFooterSection({ footerHtml, siteDetail }) {
  const currentYear = new Date().getFullYear();
  const servicePolicyHref = getLegalDocumentHref("service-policy", siteDetail);
  const privacyPolicyHref = getLegalDocumentHref("privacy-policy", siteDetail);

  return (
    <footer className=" bg-gray-100 px-5 py-7">
      {footerHtml ? (
        <div
          className="text-sm leading-7 text-gray-700"
          style={{ fontFamily: "Gmarket-M, sans-serif" }}
          dangerouslySetInnerHTML={{ __html: footerHtml }}
        />
      ) : null}
      <div className={`flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-700 ${footerHtml ? "mt-4" : ""}`}>
        {isExternalLegalHref(servicePolicyHref) ? (
          <a
            href={servicePolicyHref}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-900"
            style={{ fontFamily: "Gmarket-M, sans-serif" }}
          >
            서비스 이용약관
          </a>
        ) : (
          <Link
            to={servicePolicyHref}
            className="hover:text-gray-900"
            style={{ fontFamily: "Gmarket-M, sans-serif" }}
          >
            서비스 이용약관
          </Link>
        )}
        {isExternalLegalHref(privacyPolicyHref) ? (
          <a
            href={privacyPolicyHref}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-900"
            style={{ fontFamily: "Gmarket-M, sans-serif" }}
          >
            개인정보처리방침
          </a>
        ) : (
          <Link
            to={privacyPolicyHref}
            className="hover:text-gray-900"
            style={{ fontFamily: "Gmarket-M, sans-serif" }}
          >
            개인정보처리방침
          </Link>
        )}
      </div>
      <p
        className="mt-3 text-xs text-gray-500 tabular-nums"
        style={{ fontFamily: "Gmarket-M, sans-serif" }}
      >
        {`© ${currentYear} AmazingInfra. All rights reserved.`}
      </p>
    </footer>
  );
}

// Main SmartLanding Component
export default function SmartLanding({
  heroSlides = [],
  notices = [],
  storeInfo = {},
  isLoading = false,
}) {
  const [pageConfig, setPageConfig] = useState(null);
  const [site, siteDetail, permission] = useUserStore((state) => [
    state.site,
    state.siteDetail,
    state.permission,
  ]);
  const [carousels, setCarousels] = useState([]);
  const [noticeItems, setNoticeItems] = useState([]);
  const [couponBookItems, setCouponBookItems] = useState([]);
  const [voucherBookItems, setVoucherBookItems] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [noticeModalOpen, setNoticeModalOpen] = useState(false);
  const [noticeDetailLoading, setNoticeDetailLoading] = useState(false);
  const [noticeDetail, setNoticeDetail] = useState(null);
  const [noticeDetailError, setNoticeDetailError] = useState("");

  const storeInfoData = {
    title: siteDetail?.name || site?.name,
    description: siteDetail?.detail_info || siteDetail?.slogan || site?.slogan,
    phone: siteDetail?.telephone || site?.telephone,
    address: siteDetail?.address || site?.address,
    kakaoChannelId: siteDetail?.kakaoChannelsId || site?.kakaoChannelsId,
    typeName: siteDetail?.type?.name || site?.type?.name,
    typeImage: siteDetail?.type?.image || site?.type?.image,
    ...storeInfo,
  };

  useEffect(() => {
    if (siteDetail) {
      getPageLayout('landing', siteDetail).then(setPageConfig);
    }
  }, [siteDetail]);

  useEffect(() => {
    const fetchLandingData = async () => {
      try {
        setDataLoading(true);
        const [carouselResponse, noticeResponse, couponBookResponse, voucherBookResponse] = await Promise.all([
          API.get(`/sites/carousel/?site=${SITE_ID}`),
          noticeService.getPublishedNotices(),
          couponService.getCouponBookPublic(),
          eventService.listVoucherBookRules(),
        ]);

        setCarousels(Array.isArray(carouselResponse.data) ? carouselResponse.data : []);

        const rawNotices = noticeResponse.data?.results || noticeResponse.data || [];
        const mappedNotices = rawNotices.map((notice) => ({
          id: notice.id,
          title: notice.title,
          created_at: notice.created_at,
          date: notice.display_date || notice.created_at,
          is_important: notice.is_highlight || false,
        }));
        setNoticeItems(mappedNotices);

        const couponBooks = (couponBookResponse.data || []).filter((item) => item.state === "노출중");
        setCouponBookItems(couponBooks);

        const today = getLocalDateString();
        const voucherBooks = (voucherBookResponse.data || []).filter(
          (item) => item.state === "노출중" && item.start_date <= today && item.end_date >= today
        );
        setVoucherBookItems(voucherBooks);
      } catch (error) {
        console.error(error);
      } finally {
        setDataLoading(false);
      }
    };

    fetchLandingData();
  }, []);

  const sections = pageConfig?.sections || [];
  const showGroupReservationAction = sections.some((section) => section?.enabled && section.type === 'group-reservation-link');
  const hasStoreIntroSection = sections.some((section) => section?.enabled && section.type === 'store-intro');
  const isLayoutReady = pageConfig !== null;
  const heroSlidesData = heroSlides.length
    ? heroSlides
    : (carousels?.[0]?.images || []).map((image) => ({
      image: image.src || image.image || image.url || image,
      title: image.title || carousels?.[0]?.name || ""
    }));
  const noticeData = notices.length ? notices : noticeItems;
  const loadingState = isLoading || dataLoading || !siteDetail;

  const formatNoticeDetailDate = (dateStr) => {
    if (!dateStr) return "";
    const parsed = new Date(dateStr);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleDateString("ko-KR");
  };

  const handleNoticeClick = async (notice) => {
    if (!notice?.id) return;
    setNoticeModalOpen(true);
    setNoticeDetail(null);
    setNoticeDetailError("");
    setNoticeDetailLoading(true);

    try {
      const response = await noticeService.getNotice(Number(notice.id));
      setNoticeDetail(response.data);
    } catch (error) {
      console.error(error);
      setNoticeDetailError("공지사항을 불러오는데 실패했습니다.");
    } finally {
      setNoticeDetailLoading(false);
    }
  };

  // Render section by type
  const renderSection = (section) => {
    if (!section || !section.enabled) return null;

    switch (section.type) {
      case 'hero-carousel':
        return (
          <HeroCarouselSection
            key={section.id}
            slides={heroSlidesData}
            isLoading={loadingState}
            mainTitleColor={siteDetail?.main_title_color}
          />
        );
      case 'store-intro':
        return (
          <StoreIntroSection
            key={section.id}
            storeInfo={storeInfoData}
            siteDetail={siteDetail}
            isLoading={loadingState}
            showGroupReservationAction={showGroupReservationAction}
          />
        );
      case 'sns-links':
        return (
          <SnsLinksSection
            key={section.id}
            snsLinks={siteDetail?.sns_links || []}
            isLoading={loadingState}
          />
        );
      case 'notice-list':
        return (
          <NoticeListSection
            key={section.id}
            notices={noticeData}
            isLoading={loadingState}
            limit={section.props?.limit}
            onNoticeClick={handleNoticeClick}
          />
        );
      case 'coupon-section':
        return (
          <CouponSection
            key={section.id}
            couponBooks={couponBookItems}
            voucherBooks={voucherBookItems}
            isLoading={loadingState}
            limit={section.props?.limit}
          />
        );
      case 'group-reservation-link':
        if (hasStoreIntroSection) return null;
        return (
          <GroupReservationLinkSection
            key={section.id}
            isLoading={loadingState}
          />
        );
      case 'display-menu':
        return (
          <DisplayMenuSection
            key={section.id}
            siteId={SITE_ID}
            enabled={true}
            limit={section.props?.limit}
          />
        );
      case 'facility-grid':
        return (
          <FacilitySection
            key={section.id}
            siteId={SITE_ID}
            enabled={true}
          />
        );
      case 'review-carousel':
        return (
          <RecommendedReviewSection
            key={section.id}
            siteId={SITE_ID}
            enabled={true}
            title={siteDetail?.recommended_review_title}
          />
        );
      case 'carousel-gallery':
        return (
          <CarouselGalleryFeed
            key={section.id}
            carousels={carousels}
            isLoading={loadingState}
          />
        );
      case 'map':
        return (
          <section key={section.id} className="px-6">
            <DirectionsSection
              address={siteDetail?.address}
              siteName={siteDetail?.name}
              coordinate={siteDetail?.map_marker}
              kakaoPlaceUrl={siteDetail?.kakao_place_url}
              naverPlaceUrl={siteDetail?.naver_place_url}
            />
          </section>
        );
      default:
        return null;
    }
  };

  return (
    <MobileViewContainer>
      <Navbar activeBar={0} permission={permission} hideHeader={true} />
      {permission?.permission > 0 && (
        <div className="fixed top-3 left-1/2 -translate-x-1/2 w-full max-w-160 px-4 z-1000">
          <div className="flex justify-end">
            <a
              href={`https://${SUBDOMAIN}crm.${window.AMZ_ENV?.VITE_DOMAIN || "amazingticket.site"}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-cyan hover:bg-gray-50"
              aria-label="지점 설정"
              title="지점 설정"
            >
              <BsHouseGear size={20} />
            </a>
          </div>
        </div>
      )}
      {renderSection(sections.find(s => s.type === 'hero-carousel'))}
      <main className="flex flex-col gap-6 bg-white divide-y-8 py-6 divide-gray-200 antialiased">
        {sections.filter(s => s.type !== 'hero-carousel').map(renderSection)}
      </main>
      {isLayoutReady ? <LandingFooterSection footerHtml={site?.footer} siteDetail={siteDetail} /> : null}
      <div className="h-15" aria-hidden="true" />
      <Modal isOpen={noticeModalOpen} toggle={() => setNoticeModalOpen(false)} variant="full" className="gap-0 border-0 bg-white">
        {noticeDetailLoading ? (
          <div className="p-6 text-center">
            로딩 중...
          </div>
        ) : noticeDetailError ? (
          <div className="p-6 text-center text-gray-700">
            {noticeDetailError}
          </div>
        ) : noticeDetail ? (
          <div className="flex min-h-full flex-col">
            <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
              <button
                type="button"
                className="flex items-center justify-center w-8 h-8 rounded-full text-gray-700 hover:bg-gray-100"
                aria-label="Back"
                onClick={() => setNoticeModalOpen(false)}
              >
                <ChevronLeft size={18} />
              </button>
              <h3 className="text-base font-semibold text-gray-900">공지사항</h3>
            </div>
            <div className="relative aspect-video bg-gray-100">
              {noticeDetail.images && noticeDetail.images.length > 0 ? (
                <img
                  src={noticeDetail.images.sort((a, b) => a.sort_order - b.sort_order)[0]?.asset_url}
                  alt={noticeDetail.title}
                  className="w-full h-full object-cover outline -outline-offset-1 outline-black/10"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-700">
                  <span className="text-sm">공지사항</span>
                </div>
              )}
            </div>

            <div className="flex-1 p-6 overflow-y-auto">
              <h3 className="font-bold text-lg text-gray-900 mb-2 leading-snug text-balance">
                {noticeDetail.title}
              </h3>
              <p className="text-xs text-gray-700 mb-4">
                {formatNoticeDetailDate(noticeDetail.display_date || noticeDetail.created_at)}
              </p>

              {noticeDetail.images && noticeDetail.images.length > 1 && (
                <div className="mb-4 grid grid-cols-2 gap-2">
                  {noticeDetail.images
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .slice(1)
                    .map((img, idx) => (
                      <img
                        key={img.id || idx}
                        src={img.asset_url}
                        alt={`공지 이미지 ${idx + 2}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-100"
                      />
                    ))}
                </div>
              )}

              <div
                className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: noticeDetail.content || "" }}
              />
            </div>

            {noticeDetail.link_urls && noticeDetail.link_urls.length > 0 && (
              <div className="p-5 border-t border-gray-100 bg-gray-50/50 mt-auto">
                <div className="flex flex-col gap-2">
                  {noticeDetail.link_urls.map((link, idx) => (
                    <a
                      key={idx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-between px-4 py-2 border border-gray-300 text-gray-900 rounded-2xl bg-white hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-sm font-semibold">
                        {link.label || link.url}
                      </span>
                      <i className="ri-external-link-line ms-2 text-gray-700"></i>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            공지사항을 불러올 수 없습니다.
          </div>
        )}
      </Modal>
      <NavBottomBar activeBar={0} />
      <AnnouncementBottomSheet />
    </MobileViewContainer>
  );
}

// Export individual sections for standalone use
export {
  HeroCarouselSection,
  StoreIntroSection,
  NoticeListSection,
  CouponSection,
  SnsLinksSection,
  LandingFooterSection,
};
